// collection-item-edit.store.ts
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { computed, inject } from '@angular/core';
import { CollectionItemModel, ItemLockStatus } from '../../classes/collection-item.model';
import {
  FieldTemplateEntriesModel,
  FieldTemplateModel,
  FieldTemplateType,
} from '../../../../fields/field-template/classes/field-template.model';
import { select, Store } from '@ngrx/store';
import {
  FieldTemplatesState, getFieldTemplatesState, getItemById as getFieldTemplateById,
} from '@dash/randy/modules/crud/fields/field-template/store/reducers/field-template.reducer';
import { filter, map, switchMap, tap, distinctUntilChanged, catchError, take } from 'rxjs/operators';
import {
  CollectionItemsState,
  getItemById as getCollectionItemById,
} from '@dash/randy/modules/crud/collections/collection-item/store/reducers/collection-item.reducer';
import {
  loadCollectionItem,
  loadedCollectionItemFailed,
  updatedItemValueSuccess,
  updatedCollectionItemName,
  updatedCollectionItemValueFailed,
  updateCollectionItem,
  collectionItemUpdateSettled,
  updateCollectionItemValue,
} from '@dash/randy/modules/crud/collections/collection-item/store/actions/collection-item.actions';
import { Actions, ofType } from '@ngrx/effects';
import { EMPTY, from, of } from 'rxjs';
import { StatusService } from '@dash/randy/shared/classes/status.service';
import { COLLECTION_LOCALE } from '@dash/randy/tokens/collection-item-locale.token';
import { CognitoService } from '@dash/randy/security/service/cognito.service';
import { CollectionItemReadonlyLockService } from '@dash/randy/modules/crud/collections/collection-item/services/collection-item-readonly-lock.service';
import { CollectionItemService } from '@dash/randy/modules/crud/collections/collection-item/services/collection-item.service';
import {
  loadFieldTemplates,
  loadedFieldTemplateByIdFailed,
  loadedFieldTemplatesFailed,
} from '@dash/randy/modules/crud/fields/field-template/store/actions/field-template.actions';
import { ApplicationPermissionService } from '@dash/randy/security/service/application-permission.service';

// Include the value root because repeated groups can contain the same field ID.
const getFieldSaveKey = (valueRootId: string, itemValueId: string): string =>
  JSON.stringify([valueRootId, itemValueId]);

const hasDeprecatedTitlePanel = (collectionItem: CollectionItemModel | null): boolean => {
  const mappings = collectionItem?.usedAsMappings ?? [];
  return !mappings.some(mapping => mapping.usedAs === 'name');
};

export const CollectionItemEditSignalStore = signalStore(
  withState<{
    templateUri: string | null;
    collectionItemId: string | null;
    initialized: boolean;
    entries: FieldTemplateEntriesModel[];
    objectType: FieldTemplateType;
    searchKey: string;
    index: number;
    pageSize: number;
    loading: boolean;
    loaded: boolean;
    error: unknown | null;
    locale: string;
    currentUsername: string | null;
    lockStatus: ItemLockStatus | null;
    readOnly: boolean;
    clientReadonlyLocked: boolean;
    collectionItem: CollectionItemModel | null;
    fieldTemplate: FieldTemplateModel | null;
  }>({
    templateUri: null,
    collectionItemId: null,
    initialized: false,
    entries: [],
    objectType: 'Object' as FieldTemplateType,
    searchKey: '',
    index: 0,
    pageSize: 25,
    loading: false,
    loaded: false,
    error: null,
    locale: null,
    currentUsername: null,
    lockStatus: null,
    readOnly: false,
    clientReadonlyLocked: false,
    collectionItem: null,
    fieldTemplate: null,
  }),

  withComputed((
    store,
    applicationPermissions = inject(ApplicationPermissionService),
    statusService = inject(StatusService),
  ) => {
    const permissionType = computed(() => store.initialized() ? store.objectType() : null);
    const canManageContent = computed(() => applicationPermissions.canManageCollectionItems(permissionType()));
    const effectiveReadOnly = computed(() => store.readOnly() || !canManageContent());

    return {
      hasInit: computed(() => store.initialized()),
      hasError: computed(() => store.error() !== null),
      isObject: computed(() => store.fieldTemplate()?.type === 'Object'),
      isProcedure: computed(() => store.fieldTemplate()?.type === 'Procedure'),
      isList: computed(() => store.fieldTemplate()?.type === 'List'),
      permissionType,
      // Unlocking requires permission; editing also requires leaving read-only mode.
      canManageContent,
      effectiveReadOnly,
      canEditContent: computed(() => !effectiveReadOnly()),
      saving: statusService.pending,
      displayDeprecatedTitlePanel: computed(() => hasDeprecatedTitlePanel(store.collectionItem())),
      lockedByOtherUser: computed(() => {
        const lockStatus = store.lockStatus();
        const currentUsername = store.currentUsername();
        return !!lockStatus?.username && !!currentUsername && lockStatus.username !== currentUsername;
      }),
    };
  }),

  withMethods((
    store,
    collectionItemStore = inject(Store) as Store<CollectionItemsState>,
    fieldTemplateStore = inject(Store) as Store<FieldTemplatesState>,
    actions$ = inject(Actions),
    statusService = inject(StatusService),
    locale = inject(COLLECTION_LOCALE),
    cognitoService = inject(CognitoService),
    readonlyLockService = inject(CollectionItemReadonlyLockService),
    collectionItemService = inject(CollectionItemService),
    applicationPermissions = inject(ApplicationPermissionService),
  ) => ({
    initLocale: (newLocale: string = locale) => {
      patchState(store, { locale: newLocale });
    },
    setLocale: (newLocale: string) => {
      patchState(store, { locale: newLocale });
    },
    clearConfigState() {
      statusService.reset();
      patchState(store, state => ({
        templateUri: null,
        collectionItemId: null,
        initialized: false,
        entries: [],
        objectType: 'Object' as FieldTemplateType,
        loading: false,
        loaded: false,
        error: null,
        lockStatus: null,
        readOnly: false,
        clientReadonlyLocked: false,
        collectionItem: null,
        fieldTemplate: null,
        searchKey: state.searchKey,
        index: state.index,
        locale: state.locale,
        currentUsername: state.currentUsername,
      }));
    },
    setSearchKey: (searchKey: string) => {
      patchState(store, () => ({ searchKey }));
    },

    lockReadonly: () => {
      // Do not disable inputs while an edit is still debouncing or being saved.
      if (statusService.pending()) {
        return;
      }
      const collectionItemId = store.collectionItemId();
      if (!collectionItemId) {
        return;
      }
      readonlyLockService.lock(collectionItemId);
      patchState(store, { readOnly: true, clientReadonlyLocked: true });
    },
    unlockReadonly: async () => {
      if (!store.canManageContent()) {
        patchState(store, { readOnly: true });
        return false;
      }
      const collectionItemId = store.collectionItemId();
      if (!collectionItemId) {
        return false;
      }

      try {
        const lockStatus = await collectionItemService.setLockStatus(collectionItemId);
        let currentUsername = store.currentUsername();
        if (!currentUsername) {
          const user = await cognitoService.getUser().catch(() => null);
          currentUsername = user?.username ?? null;
          patchState(store, { currentUsername });
        }
        const lockedByOtherUser = !!lockStatus?.username && (!currentUsername || lockStatus.username !== currentUsername);

        patchState(store, { lockStatus });
        if (lockedByOtherUser) {
          patchState(store, { readOnly: true, clientReadonlyLocked: true });
          return false;
        }

        readonlyLockService.unlock(collectionItemId);
        patchState(store, { readOnly: false, clientReadonlyLocked: false });
        return true;
      } catch (e) {
        const lockStatus = await collectionItemService.getLockStatus(collectionItemId).catch(() => null);
        if (lockStatus) {
          patchState(store, { lockStatus });
        }
        patchState(store, { readOnly: true, clientReadonlyLocked: true });
        return false;
      }
    },
    setIndex: (index: number) => patchState(store, state => ({ index: Number(index) })),
    setPageSize: (pageSize: number) => patchState(store, state => ({ pageSize: Number(pageSize) })),
    setIndexFromRoute: rxMethod<number>(index$ => index$.pipe(
      filter(index => index === 0 || index > 0),
      tap(index => patchState(store, { index: Number(index) })),
    )),

    setLockStatus: (lockStatus: ItemLockStatus) => patchState(store, () => ({ lockStatus })),
    enableReadonly: () => patchState(store, { readOnly: true }),
    disableReadonly: () => {
      if (store.canManageContent()) {
        patchState(store, { readOnly: false });
      }
    },
    loadCurrentUser: rxMethod<void>(trigger$ => trigger$.pipe(
      switchMap(() => from(cognitoService.getUser()).pipe(
        tap(user => patchState(store, { currentUsername: user?.username ?? null })),
        catchError(() => {
          patchState(store, { currentUsername: null });
          return EMPTY;
        }),
      )),
    )),
    watchSaves: rxMethod<void>(trigger$ => trigger$.pipe(
      switchMap(() => actions$.pipe(
        ofType(
          updateCollectionItem, updateCollectionItemValue,
          collectionItemUpdateSettled, updatedItemValueSuccess, updatedCollectionItemName,
          updatedCollectionItemValueFailed,
        ),
        filter(action => {
          const id = 'collectionItem' in action ? action.collectionItem?.id : action.collectionItemId;
          return id === store.collectionItemId();
        }),
        tap(action => {
          switch (action.type) {
            case updateCollectionItem.type:
              statusService.startSave('record');
              break;
            case updateCollectionItemValue.type:
              statusService.replaceSave(
                getFieldSaveKey(action.valueRootId ?? action.valueRoot?.id ?? '', action.itemValue?.id ?? ''),
              );
              break;
            case collectionItemUpdateSettled.type:
              statusService.finishSave('record', action.failed);
              break;
            case updatedCollectionItemName.type:
              statusService.finishSave(getFieldSaveKey('general', 'name'));
              break;
            case updatedItemValueSuccess.type:
            case updatedCollectionItemValueFailed.type:
              statusService.finishSave(
                getFieldSaveKey(action.valueRootId, action.itemValueId),
                action.type === updatedCollectionItemValueFailed.type,
              );
              break;
          }
        }),
      )),
    )),
    watchLoadFailure: rxMethod<void>(trigger$ => trigger$.pipe(
      switchMap(() => actions$.pipe(
        ofType(loadedCollectionItemFailed, loadedFieldTemplatesFailed, loadedFieldTemplateByIdFailed),
        filter(action => {
          if ('collectionItemId' in action) {
            return !!action.collectionItemId && action.collectionItemId === store.collectionItemId();
          }
          if ('id' in action) {
            return action.id === store.collectionItem()?.templateId;
          }
          return !store.fieldTemplate();
        }),
        tap(action => patchState(store, {
          loading: false,
          loaded: false,
          error: action.error,
        })),
      )),
    )),
    retryLoad: () => {
      const collectionItemId = store.collectionItemId();
      if (!collectionItemId) {
        return;
      }

      patchState(store, {
        loading: true,
        loaded: false,
        error: null,
      });
      collectionItemStore.dispatch(loadCollectionItem({ collectionItemId }));
      fieldTemplateStore.dispatch(loadFieldTemplates());
    },
    selectCollectionItem: rxMethod<string>(collectionItemId$ => collectionItemId$.pipe(
      filter((collectionItemId): collectionItemId is string => !!collectionItemId && collectionItemId.length > 0),
      distinctUntilChanged(),
      tap(collectionItemId => {
        statusService.reset();
        const clientReadonlyLocked = readonlyLockService.isLocked(collectionItemId);
        patchState(store, {
          collectionItemId,
          initialized: false,
          loading: true,
          loaded: false,
          error: null,
          collectionItem: null,
          fieldTemplate: null,
          templateUri: null,
          entries: [],
          objectType: 'Object' as FieldTemplateType,
          lockStatus: null,
          readOnly: clientReadonlyLocked,
          clientReadonlyLocked,
        });
        collectionItemStore.dispatch(loadCollectionItem({ collectionItemId }));
      }),
      switchMap(collectionItemId =>
        collectionItemStore.pipe(
          select(getCollectionItemById(collectionItemId)),
          filter((collectionItem): collectionItem is CollectionItemModel => !!collectionItem),
          switchMap(collectionItem =>
            fieldTemplateStore.pipe(
              select(getFieldTemplatesState),
              tap(fieldTemplateState => {
                if (fieldTemplateState.error) {
                  patchState(store, {
                    loading: false,
                    loaded: false,
                    error: fieldTemplateState.error,
                  });
                }
              }),
              map(fieldTemplateState => fieldTemplateState.entities[collectionItem.templateId]),
              filter((fieldTemplate): fieldTemplate is FieldTemplateModel => !!fieldTemplate),
              map(fieldTemplate => ({ collectionItem, fieldTemplate })),
            ),
          ),
          tap(({ collectionItem, fieldTemplate }) => {
            const clientReadonlyLocked = readonlyLockService.isLocked(collectionItem.id);

            patchState(store, {
              collectionItem: { ...collectionItem },
              fieldTemplate,
              templateUri: fieldTemplate.typeUri,
              collectionItemId: collectionItem.id,
              entries: fieldTemplate.entries,
              objectType: fieldTemplate.type,
              readOnly: clientReadonlyLocked || !applicationPermissions.canManageCollectionItems(fieldTemplate.type),
              clientReadonlyLocked,
              initialized: true,
              loading: false,
              loaded: true,
              error: null,
            });
          }),
          catchError(error => {
            patchState(store, {
              loading: false,
              loaded: false,
              error,
            });
            return of(null);
          }),
        ),
      ),
    )),
    getEntryByFieldRootId: (id: string) => computed(() => store.entries().find(e => e.fieldRootId === id)),
    // todo: Validate this method, it seems that they keys arent the same
    getUriByFieldRootIdAndFieldId: (fieldRootId: string, fieldId: string) =>
      computed(() => {
        const entry = store.entries().find(e => e.fieldRootId === fieldRootId);
        if (!entry) {
          return null;
        }
        return entry.fields.find(f => f.id === fieldId)?.uri || null;
      }),
    setCollectionItem: (collectionItem: CollectionItemModel) => {
      patchState(store, () => ({ collectionItem }));

      if (collectionItem.templateId) {
        fieldTemplateStore.pipe(
          select(getFieldTemplateById(collectionItem.templateId)),
          filter((template): template is FieldTemplateModel => !!template),
          take(1),
        ).subscribe(template => {
          patchState(store, () => ({ fieldTemplate: template }));
        })
      }

    },
  })),
);

export type CollectionItemEditSignalStoreInstance = InstanceType<typeof CollectionItemEditSignalStore>;
